import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ScanLine, CheckCircle2, XCircle, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

// Deep-link landing for any AfriGate QR. Scanning a label with any phone camera
// opens this page, which authenticates the operator and performs the real action.
export default function ScanResolve() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", msg: "Reading code…" });

  useEffect(() => {
    if (user === null) return; // auth still checking
    if (user === false) { nav(`/admin/login?next=${encodeURIComponent(`/s/${code}`)}`, { replace: true }); return; }
    if (user.role !== "admin") { setState({ status: "error", msg: "Admin access is required to scan." }); return; }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/resolve/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (data.type === "item") {
          await api.post("/admin/sourcing/scan", { code });
          setState({ status: "ok", msg: `Marked "${data.name}" as bought` });
          setTimeout(() => nav("/admin?tab=sourcing", { replace: true }), 1200);
        } else if (data.type === "order") {
          setState({ status: "ok", msg: `Opening order ${data.ref}` });
          setTimeout(() => nav(`/admin?tab=tracking&order=${encodeURIComponent(data.ref)}`, { replace: true }), 700);
        } else if (data.type === "package") {
          setState({ status: "ok", msg: `Opening package ${data.ref}` });
          setTimeout(() => nav("/admin?tab=tracking", { replace: true }), 700);
        } else {
          setState({ status: "ok", msg: `Opening shipment ${data.ref}` });
          setTimeout(() => nav("/admin?tab=shipping", { replace: true }), 700);
        }
      } catch (e) {
        if (!cancelled) setState({ status: "error", msg: e?.response?.data?.detail || "This code was not recognised." });
      }
    })();
    return () => { cancelled = true; };
  }, [user, code, nav]);

  const Icon = state.status === "ok" ? CheckCircle2 : state.status === "error" ? XCircle : Loader2;
  const tone = state.status === "ok" ? "text-[#2D7A5D]" : state.status === "error" ? "text-[#B23B3B]" : "text-[#306D29]";

  return (
    <div className="min-h-screen bg-[#0D530E] flex items-center justify-center px-5" data-testid="scan-resolve">
      <div className="bg-[#FBF5DD] rounded-3xl p-9 max-w-sm w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-full bg-[#306D29] flex items-center justify-center text-white"><Globe size={18} /></span>
          <span className="font-display text-2xl font-bold text-[#0D530E]">AfriGate Ops</span>
        </div>
        <div className="flex flex-col items-center gap-3">
          <ScanLine size={22} className="text-[#306D29]" />
          <p className="font-mono text-sm text-[#0D530E] break-all">{code}</p>
          <Icon size={40} className={`${tone} ${state.status === "loading" ? "animate-spin" : ""} mt-2`} />
          <p className={`font-medium ${tone}`} data-testid="scan-result-msg">{state.msg}</p>
          {state.status === "error" && (
            <button onClick={() => nav("/admin", { replace: true })} data-testid="scan-back-btn"
              className="mt-3 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-2.5 rounded-full" style={{ transition: "background-color 0.2s ease" }}>
              Go to console
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
